import $ from 'jquery';
import 'core-js/features/array/flat';
import SmoothScroll from 'smooth-scroll';
import compose from 'ramda/es/compose';
import join from 'ramda/es/join';
import map from 'ramda/es/map';
import Sorter from '../ui/sorter';
import Filters from '../ui/filters';
import Pagination from '../ui/pagination';
import {
  htmlPrefetcher,
  htmlStringToChildren,
  classify,
  URLManager,
} from './utils';

const namespace = 'collection';
const addedFilterClass = classify(['is-hidden']);
const getItems = htmlStringToChildren('.product-listing__item');

const defaults = {
  selectors: {
    target: '#collection-products',
    list: '#collection-products .product-listing__list',
    loader: '.collection-loader',
  },
  classes: {
    loading: 'is-loading',
    visible: 'is-visible',
    filtered: 'is-filtered',
  },
};

export class Collection {
  state = {
    fetchUrl: '',
    previousFetchUrl: '',
    resolved: false,
    pending: false,
    filterClickCount: 0,
  };

  constructor(el, options) {
    this.$el = $(el);
    this.el = this.$el.get(0);

    this.options = $.extend(
      true,
      defaults,
      {
        ...this.el.dataset,
      },
      options,
    );

    this.baseUrl = this.options.url;
    this.sorter = new Sorter('.js-sorter', {
      isControlled: true,
    });
    this.filters = new Filters('.filters', {
      isControlled: true,
    });
    this.pagination = new Pagination({
      isControlled: true,
    });
    this.scroll = new SmoothScroll();
    this.urlManager = new URLManager({
      reqParams: new window.URLSearchParams([['view', 'ajax']]),
      reqWhitelist: ['view', 'sort_by', 'page'],
      hashBlacklist: ['page'],
      baseUrl: this.baseUrl,
    });

    if (!this.baseUrl || !this.options.pagecount) {
      return;
    }

    this.initialPage = this.options.pagecurrent;
    this.pageRange = [
      ...Array(parseInt(this.options.pagecount, 10) + 1).keys(),
    ].slice(1);

    this.bind();
    this.check();
    this.updateHreflangs();
    window.history.replaceState({}, '', window.location.href);
  }

  bind() {
    this.$el.on('filters:applied', this.handleFilters.bind(this));
    this.$el.on('sorter:changed', this.handleSorter.bind(this));
    $(document).on('pagination:updated', this.handlePager.bind(this));
    $(document).on('filters:changed', this.handleFilterChange.bind(this));
    $(window).on('popstate', this.handlePopState.bind(this));
  }

  check() {
    const params = URLManager.getParams();
    const urls = this.createPageUrls();

    if (!URLManager.isHash()) {
      return;
    }

    this.restore(urls, params);
  }

  /**
   * @param {Event} event
   */
  handlePopState(event) {
    const params = URLManager.getParams();

    /**
     * Ignore null state objects
     * to avoid popstate on pageload
     * in Safari
     */
    if (!event.originalEvent.state) {
      return;
    }

    if (!URLManager.isHash()) {
      this.request(window.location.href, false);
      this.restoreToolbar(params);
      this.$el.removeClass(this.options.classes.filtered);
      return;
    }

    // Check for dataset changes
    if (this.state.fetchUrl) {
      const lastSearch = new window.URL(this.state.fetchUrl).search;
      const lastParams = URLManager.getParams(lastSearch);

      if (lastParams.get('sort_by') === params.get('sort_by')) {
        this.update(params);
        return;
      }
    }

    this.check();
  }

  /**
   * Handle when a filter is toggled
   */
  handleFilterChange() {
    const urls = this.createPageUrls();

    if (this.state.filterClickCount >= 1) {
      return;
    }

    this.state.filterClickCount = this.state.filterClickCount + 1;

    if (!this.shouldPrefetch(urls)) {
      return;
    }

    this.prefetch(urls)
      .then(result => {
        if (this.state.resolved || !this.state.pending) {
          return result;
        }

        const {tags, url} = this.getFilterData();

        this.replace(this.prefetchResults);
        this.applyTags(tags, url);
        return result;
      })
      .catch(error => {
        window.console.log(error);
      });
  }

  /**
   * Handle filter apply action
   */
  handleFilters() {
    const {tags, url} = this.getFilterData();
    const tagLength = tags.flat().length;

    if (tagLength <= 0) {
      this.reset();
      return;
    }

    if (this.prefetchResults && this.shouldFetch()) {
      this.replace(this.prefetchResults);
      this.applyTags(tags, url);
      return;
    }

    if (!this.shouldFetch() && this.state.resolved) {
      this.applyTags(tags, url);
      return;
    }

    this.showLoader();
    this.state.pending = true;
  }

  applyTags(tags, url) {
    this.$el.addClass(this.options.classes.filtered);
    this.mix(tags);
    this.complete(url);
    this.showLoader(false);
    this.state.resolved = true;
    this.state.pending = false;
    this.state.filterClickCount = 0;
  }

  /**
   * @param {*} _
   * @param {Object} data
   */
  handleSorter(_, data) {
    const {params} = data;

    if (!URLManager.isHash()) {
      params.set('page', '');
    }

    const updatedParams = URLManager.mergeSearchParams([
      URLManager.getParams(),
      params,
    ]);

    if (URLManager.isHash()) {
      const urls = this.createPageUrls(params);
      const updatedUrl = this.urlManager.createHashUrl([Array.from(params)]);

      this.restore(urls, updatedParams);
      this.complete(updatedUrl);
      return;
    }

    this.request(
      `${window.location.origin}${window.location.pathname}${
        updatedParams.toString() ? `?${updatedParams}` : ''
      }`,
    );
  }

  /**
   * @param {*} _
   * @param {Object} data
   */
  handlePager(_, data) {
    const {uri} = data;

    this.request(`${window.location.origin}${uri}`).always(() =>
      this.scrollTo(),
    );
  }

  /**
   * Retrieve current filter data
   *
   * @returns {Object}
   */
  getFilterData() {
    const tagGroups = this.filters.getGroupedVals();
    const tags = tagGroups.map(group => group.vals);
    const sequence = this.createTagSequence(tagGroups);
    const url = this.urlManager.createHashUrl([sequence]);

    return {
      tags,
      url,
    };
  }

  /**
   * @returns {Boolean}
   */
  shouldFetch() {
    return this.state.fetchUrl !== this.state.previousFetchUrl;
  }

  /**
   * @param {Array} urls
   * @returns {Boolean}
   */
  shouldPrefetch(urls) {
    return (
      !this.prefetchResults ||
      !htmlPrefetcher.checkAll(urls).filter(Boolean).length
    );
  }

  /**
   * @param {URLSearchParams} customParams
   * @returns {String[]}
   */
  createPageUrls(customParams) {
    const params = URLManager.getParams();
    params.delete('page');

    return this.pageRange.map(page =>
      this.urlManager.createRequestUrl([params, {page}, customParams]),
    );
  }

  /**
   * @param {Array} data
   * @returns {Array[]}
   */
  createTagSequence(data = []) {
    return data
      .map(item => {
        const {name = '', vals = []} = item;
        const values = vals.join('+');

        return [name, values];
      })
      .filter(Boolean);
  }

  /**
   * Applies a query params object to
   * the product listing
   *
   * @param {URLSearchParams} params
   */
  mixParams(params) {
    const sequence = Array.from(params);
    const tagSequence = sequence
      .filter(item => item[0] !== 'sort_by')
      .map(item => item.slice(1));
    const tags = tagSequence.map(item => item.toString().split('+'));

    this.mix(tags);
  }

  /**
   * Matches & decorates product elements against a sequence
   *
   * @param {Array[]} sequence
   */
  mix(sequence) {
    const elementTags = this.getElementsWithTags();
    const matchData = this.getMatchData(sequence, elementTags);

    this.prune(matchData);
    this.disableFiltersByValue(elementTags);
    this.showEmpty(matchData.matched.length <= 0);
  }

  /**
   * Given a sequence and tags to match,
   * get all matched and unmatched elements
   *
   * @param {Array[]} sequence
   * @param {Object[]} tags
   * @returns {Object}
   */
  getMatchData(sequence, tags) {
    return tags.reduce(
      (accu, curr) => {
        const hasMatch = this.matches(sequence, curr.tags);
        const matched = hasMatch
          ? [...accu.matched, curr.$element]
          : [...accu.matched];
        const unmatched = hasMatch
          ? [...accu.unmatched]
          : [...accu.unmatched, curr.$element];

        return {
          matched,
          unmatched,
        };
      },
      {
        matched: [],
        unmatched: [],
      },
    );
  }

  /**
   * Get tags for all products in the list
   *
   * @returns {Object[]}
   */
  getElementsWithTags() {
    const $list = $(this.options.selectors.list, this.$el);
    const $elements = $list.children();

    return $elements.get().reduce((accu, curr) => {
      const $element = $(curr);
      const tagsString = $element.data('tags');

      return [
        ...accu,
        {
          $element,
          tags: tagsString,
        },
      ];
    }, []);
  }

  /**
   * Hide / Show products based on match condition
   *
   * @param {Object}
   */
  prune({matched, unmatched}) {
    matched.forEach(el =>
      $(el)
        .addClass('is-visible')
        .removeClass('is-hidden'),
    );
    unmatched.forEach(el =>
      $(el)
        .addClass('is-hidden')
        .removeClass('is-visible'),
    );
  }

  /**
   * Disable filter options within their corresponding groups
   *
   * @param {Array[]} tags
   */
  disableFiltersByValue(tags) {
    const allFilters = this.filters.getGroupedOptions();
    const currentFilters = this.filters.getGroupedVals();

    const disabledGroupValues = allFilters.map(group => {
      const {vals = []} = group;

      const newVals = vals.filter(val => {
        const current = this.createMergedGroup(allFilters, currentFilters, val);
        const sequence = current.map(item => item.vals);
        const matchData = this.getMatchData(sequence, tags);
        return matchData.matched.length <= 0;
      });

      return {
        ...group,
        vals: newVals,
      };
    });

    this.filters.disableGroupedVals(disabledGroupValues);
  }

  /**
   * Merge a value into it's relevant
   * sequence group
   *
   * @param {Object[]} master
   * @param {Object[]} data
   * @param {String} value
   */
  createMergedGroup(master, data, value) {
    return data.map(group => {
      const matchingGroup = master.filter(groupOptions =>
        groupOptions.vals.includes(value),
      )[0];

      if (matchingGroup.name === group.name) {
        return {
          ...group,
          vals: [...group.vals, value],
        };
      }

      return group;
    });
  }

  /**
   * applyTags if atleast one value in every sequence
   * group exists in a string
   *
   * @param {Array[]} sequence
   * @param {String} tags
   * @returns {Boolean}
   */
  matches(sequence, tags) {
    return sequence.every(vals => {
      if (!vals.length) {
        return true;
      }
      return vals.some(val => this.match(val, tags));
    });
  }

  /**
   * @param {String} tag
   * @param {String} string
   * @returns {Boolean}
   */
  match(tag, string) {
    const reg = new RegExp(` ${tag} `, 'g');
    return string.match(reg);
  }

  /**
   * @param {String[]} urls
   * @returns {Promise}
   */
  prefetch(urls) {
    this.state.resolved = false;

    return htmlPrefetcher
      .request(urls)
      .then(
        compose(
          map(addedFilterClass),
          getItems,
          join(''),
        ),
      )
      .then(result => {
        this.prefetchResults = result;
        this.state.previousFetchUrl = this.state.fetchUrl;
        this.state.fetchUrl = urls[urls.length - 1];
        return result;
      })
      .catch(error => window.console.log(error));
  }

  /**
   * @param {String[]} urls
   * @param {URLSearchParams} params
   * @returns {String[]}
   */
  async restore(urls, params) {
    this.showLoader();
    const results = await this.prefetch(urls);

    this.update(params, results);
    this.showLoader(false);
    return results;
  }

  /**
   * @param {URLSearchParams} params
   * @param {String} replacement
   */
  update(params, replacement) {
    this.restoreToolbar(params);

    if (replacement) {
      this.replace(replacement);
    }

    this.mixParams(params);
    this.$el.addClass(this.options.classes.filtered);
  }

  /**
   * @param {URLSearchParams} params
   */
  restoreToolbar(params) {
    const sort = params.get('sort_by');
    const updatedVals = this.paramsToGroupVals(params);

    this.filters.resetVals();
    this.filters.setGroupVals(updatedVals);
    this.sorter.setOption(sort);
  }

  /**
   * @param {URLSearchParams} params
   */
  paramsToGroupVals(params) {
    const filterGroups = this.filters.getGroupedVals();

    return filterGroups
      .map(object => {
        const {name} = object;
        const vals = params.get(name);

        if (!vals) {
          return '';
        }

        object.vals = vals.split('+');
        return object;
      })
      .filter(Boolean);
  }

  /**
   * Restore collection to page load state
   */
  reset() {
    const params = URLManager.getParams();
    const sort = params.get('sort_by');

    this.request(`${this.baseUrl}${sort ? `?sort_by=${sort.toString()}` : ''}`);
    this.$el.removeClass(this.options.classes.filtered);

    this.filters.enableGroupedVals();
  }

  /**
   * @returns {Promise}
   */
  request(url, location = true) {
    if (!url) {
      return Promise.resolve();
    }

    this.showLoader();

    return $.get(url)
      .done(data => {
        const $html = $(this.options.selectors.target, data);

        this.render($html);

        if (location) {
          this.complete(url);
        }
      })
      .fail(this.showLoader.bind(this, false));
  }

  /**
   * @param {String} url
   */
  complete(url) {
    this.$el.trigger(`${namespace}:updated`);
    window.history.pushState({}, '', url);
    this.updateHreflangs();
  }

  /**
   * Enable/Disable the loader
   * @param {Boolean} state
   */
  showLoader(state = true) {
    this.$el.toggleClass(this.options.classes.loading, state);
    $(this.options.selectors.loader).toggleClass(
      this.options.classes.visible,
      state,
    );
  }

  /**
   * @param {String} html
   */
  replace(html) {
    $(this.options.selectors.list)
      .empty()
      .append(html);
  }

  /**
   * @param {String} html
   */
  render(html) {
    $(this.options.selectors.target).replaceWith(html);
    $(this.options.selectors.loader).removeClass(this.options.classes.visible);
    this.$el.removeClass(this.options.classes.loading);
  }

  /**
   * Show / Hide the empty state
   *
   * @param {Boolean} show
   */
  showEmpty(show = true) {
    const $target = $(this.options.selectors.target);
    const $empty = $('#empty-note');
    const el = $('<div id="empty-note" />').text(this.options.empty);

    if (!show) {
      $('#empty-note', $target).remove();
      return;
    }

    if ($empty.length) {
      return;
    }

    $target.prepend(el);
  }

  /**
   * Scroll to an element
   * @param {Element} el
   */
  scrollTo(el = this.el) {
    this.scroll.animateScroll(el, null, {
      updateURL: false,
    });
  }

  updateHreflangs() {
    const hreflangs = document.querySelectorAll("link[rel='alternate']");
    const params = new URLSearchParams(window.location.search.substring(1));
    let query = '';

    // If the page query parameter exists and is not 1, create a query parameter
    if (params.has('page') && Array.from(params).length === 1) {
      const page = params.get('page');

      // Ignore a page value of 1, in order to avoid a duplicate url for the first page
      if (parseInt(page) && parseInt(page) > 1) {
        query = '?page=' + page;
      }
    }
    hreflangs.forEach(link => {
      // Remove any previously appended query strings
      let url = link.getAttribute('href').split('?')[0];
      url += query;
      link.setAttribute('href', url);
    });
  }
}

class ProductCardForm extends HTMLElement {
  constructor() {
    super()

    this.addToCart  = this.querySelector('[data-add-to-cart]')
    this.addToCart?.addEventListener('click', this.addProduct.bind(this))
    this.atcBtnText = this.querySelector('[data-add-to-cart-text]')
    this.cartCount = document.querySelector('[data-cart]')
  }

  addProduct(event) {
    event.preventDefault()

    const productId = this.querySelector('[name="id"]').value
    const body = {
      items: [
        {
          id: productId,
          quantity: 1
        },
      ],
    };
    this.setButtonLabel('adding');

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    })
      .then((response) => response.json())
      .then((data) => {
        this.afterCartUpdate()
        this.setButtonLabel('added')
      });
  }

  afterCartUpdate() {
    fetch('/cart.js')
      .then((response) => response.json())
      .then((data) => {
        this.cartCount.querySelector('.cart__count').innerHTML = data.item_count
        this.cartCount.classList.add('has-updated')
        setTimeout(() => {
          this.cartCount.classList.remove('has-updated')
          this.setButtonLabel()
        }, 1500)

        setTimeout(() => {
          this.closeActiveProductCardForms()
        }, 2500)
      })
  }

  setButtonLabel(state = "default") {
    let label = ''
    switch(state) {
      case 'adding':
        label = theme.strings.quickAdd.addingToCart
        break
    
      case 'added':
        label = theme.strings.quickAdd.addedToCart
        break

      default:
        label = theme.strings.quickAdd.addToCart
    }
    this.atcBtnText.innerHTML = label
  }

  closeActiveProductCardForms() {
    const quickBuyForms = document.querySelectorAll('.quick-buy-form')
    quickBuyForms.forEach((quickBuyForm) => { quickBuyForm.classList.remove('active') })
    document.querySelector('body').classList.remove('popup-active')
  }
}

customElements.define('product-card-form', ProductCardForm)
